import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChatService } from '../../../services/chat.service';
import { AuthService } from '../../../services/auth.service';
import { ChatMessage } from '../../../models/chat-message.model';

export interface MessageDialogData {
  missionId?: number;
  missionTitle?: string;
  establishmentName?: string;
  agencyId?: number;
}

@Component({
  selector: 'app-message-dialog',
  templateUrl: './message-dialog.component.html',
  styleUrls: ['./message-dialog.component.scss']
})
export class MessageDialogComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  messageText = '';
  selectedFile: File | null = null;
  isRecording = false;
  isSending = false;
  showSuccess = false;
  showError = false;
  errorMessage = '';
  maxLength = 500;

  // Voice recording
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  recordingDuration = 0;
  recordingInterval: any;

  constructor(
    public dialogRef: MatDialogRef<MessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MessageDialogData,
    private chatService: ChatService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // Connect to chat if not already connected
    const user = this.authService.user;
    if (user && user.agency_id) {
      this.chatService.connect(user.collary_id, user.agency_id);
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'video/mp4', 'video/webm', 'video/ogg'];
      if (!validTypes.includes(file.type)) {
        this.showErrorMessage('Seuls les fichiers PNG, JPEG, JPG, PDF et vidéos sont autorisés');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.showErrorMessage('La taille du fichier ne doit pas dépasser 5 Mo');
        return;
      }

      this.selectedFile = file;
      this.sendFileAttachment();
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  sendFileAttachment(): void {
    if (!this.selectedFile) {
      return;
    }

    const user = this.authService.user;
    if (!user || !user.agency_id) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];

      let attachmentType: 'IMAGE' | 'PDF' | 'VIDEO' = 'IMAGE';
      if (this.selectedFile!.type === 'application/pdf') {
        attachmentType = 'PDF';
      } else if (this.selectedFile!.type.startsWith('video/')) {
        attachmentType = 'VIDEO';
      }

      const missionRef = this.data.missionTitle && this.data.missionId
        ? `Mission ref : ${this.data.missionTitle}`
        : this.data.missionTitle || '';

      const message: any = {
        senderId: user.collary_id,
        senderType: 'CANDIDAT',
        senderName: user.email || 'Candidat',
        candidatId: user.collary_id,
        agencyId: user.agency_id,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: attachmentType,
        attachmentPath: base64Data,
        attachmentFileName: this.selectedFile!.name,
        missionId: this.data.missionId,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);
      this.selectedFile = null;

      this.showSuccess = true;
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    };

    reader.onerror = () => {
      this.showErrorMessage('Erreur lors de la lecture du fichier');
      this.isSending = false;
    };

    reader.readAsDataURL(this.selectedFile);
  }

  startRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
      return;
    }

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingDuration = 0;

        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          this.sendVoiceMessage(audioBlob);
          stream.getTracks().forEach(track => track.stop());
          clearInterval(this.recordingInterval);
        };

        this.mediaRecorder.start();
        this.isRecording = true;

        // Start duration counter
        this.recordingInterval = setInterval(() => {
          this.recordingDuration++;
        }, 1000);
      })
      .catch(error => {
        this.showErrorMessage('Impossible d\'accéder au microphone');
        console.error('Error accessing microphone:', error);
      });
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  sendVoiceMessage(audioBlob: Blob): void {
    const user = this.authService.user;
    if (!user || !user.agency_id) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];

      const missionRef = this.data.missionTitle && this.data.missionId
        ? `Mission ref : ${this.data.missionTitle}`
        : this.data.missionTitle || '';

      const message: any = {
        senderId: user.collary_id,
        senderType: 'CANDIDAT',
        senderName: user.email || 'Candidat',
        candidatId: user.collary_id,
        agencyId: user.agency_id,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: 'VOICE',
        attachmentPath: base64Data,
        attachmentFileName: `voice_${Date.now()}.webm`,
        missionId: this.data.missionId,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);

      this.showSuccess = true;
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    };

    reader.onerror = () => {
      this.showErrorMessage('Erreur lors de l\'envoi du message vocal');
      this.isSending = false;
    };

    reader.readAsDataURL(audioBlob);
  }

  sendMessage() {
    if (!this.messageText.trim() || this.isSending) {
      return;
    }

    const user = this.authService.user;
    if (!user || !user.agency_id) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    try {
      const missionRef = this.data.missionTitle && this.data.missionId
        ? `Mission ref : ${this.data.missionTitle}`
        : this.data.missionTitle || '';

      const message: ChatMessage = {
        senderId: user.collary_id,
        senderType: 'CANDIDAT',
        senderName: user.email || 'Candidat',
        candidatId: user.collary_id,
        agencyId: user.agency_id,
        message: this.messageText.trim(),
        timestamp: new Date(),
        isRead: false,
        missionId: this.data.missionId,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);

      this.showSuccess = true;
      this.messageText = '';

      // Close dialog after a short delay
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    } catch (error) {
      this.showErrorMessage('Erreur lors de l\'envoi du message');
      this.isSending = false;
    }
  }

  showErrorMessage(message: string) {
    this.errorMessage = message;
    this.showError = true;
    setTimeout(() => {
      this.showError = false;
    }, 3000);
  }

  get remainingChars(): number {
    return this.maxLength - this.messageText.length;
  }

  close() {
    this.dialogRef.close();
  }
}